/**
 * DPIPersistenceService - Persist DPI settings across browser refreshes
 * 
 * This service ensures that DPI changes made through the Page Setup Dialog
 * are preserved when the user refreshes the browser, preventing the Canvas
 * from resetting to the default 96 DPI.
 */

const DPI_STORAGE_KEY = 'edgedocs-client-first-dpi';
const DEFAULT_DPI = 96;

export class DPIPersistenceService {
  /**
   * Detect actual display DPI using canvas measurement
   */
  static detectDisplayDPI(): number {
    try {
      // ✅ DPI FIX: More robust DPI detection
      // Use devicePixelRatio as fallback if canvas method fails
      const devicePixelRatio = window.devicePixelRatio || 1;
      const estimatedDPI = Math.round(96 * devicePixelRatio);
      
      console.log(`🔍 [DPI_DETECTION] Device pixel ratio: ${devicePixelRatio}, estimated DPI: ${estimatedDPI}`);
      
      // Try canvas method first
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        console.warn('⚠️ [DPI_DETECTION] Canvas context not available, using devicePixelRatio fallback');
        return estimatedDPI >= 72 && estimatedDPI <= 600 ? estimatedDPI : DEFAULT_DPI;
      }

      // Set canvas size to 1 inch (96 CSS pixels)
      canvas.width = 96;
      canvas.height = 96;
      canvas.style.width = '1in';
      canvas.style.height = '1in';
      
      // Add to DOM temporarily for measurement
      canvas.style.position = 'absolute';
      canvas.style.left = '-9999px';
      canvas.style.top = '-9999px';
      document.body.appendChild(canvas);

      // Measure the actual pixel dimensions
      const rect = canvas.getBoundingClientRect();
      const actualDPI = Math.round(rect.width);
      
      // Clean up
      document.body.removeChild(canvas);
      
      console.log(`🔍 [DPI_DETECTION] Canvas detected DPI: ${actualDPI}`);
      
      // Validate DPI range (72-600 is reasonable for displays)
      if (actualDPI >= 72 && actualDPI <= 600) {
        return actualDPI;
      } else {
        console.warn(`⚠️ [DPI_DETECTION] Invalid canvas DPI: ${actualDPI}, using devicePixelRatio fallback: ${estimatedDPI}`);
        return estimatedDPI >= 72 && estimatedDPI <= 600 ? estimatedDPI : DEFAULT_DPI;
      }
    } catch (error) {
      console.warn('⚠️ [DPI_DETECTION] Error detecting DPI:', error);
      // Fallback to devicePixelRatio
      const devicePixelRatio = window.devicePixelRatio || 1;
      const estimatedDPI = Math.round(96 * devicePixelRatio);
      return estimatedDPI >= 72 && estimatedDPI <= 600 ? estimatedDPI : DEFAULT_DPI;
    }
  }

  /**
   * Get DPI with auto-detection if not saved
   */
  static getDPI(): number {
    const savedDPI = this.loadDPI();
    
    // If no DPI is saved, defer detection until DOM is ready
    if (savedDPI === DEFAULT_DPI) {
      // ✅ DPI FIX: Defer DPI detection until DOM is ready
      // This prevents the "Invalid detected DPI: 0" error during component initialization
      setTimeout(() => {
        const detectedDPI = this.detectDisplayDPI();
        if (detectedDPI !== DEFAULT_DPI) {
          this.saveDPI(detectedDPI);
          console.log(`💾 [DPI_AUTO_DETECT] Auto-detected and saved DPI: ${detectedDPI}`);
        }
      }, 100); // Small delay to ensure DOM is ready
    }
    
    return savedDPI;
  }
  /**
   * Save DPI setting to localStorage
   */
  static saveDPI(dpi: number): void {
    try {
      if (dpi && dpi >= 72 && dpi <= 600) {
        localStorage.setItem(DPI_STORAGE_KEY, dpi.toString());
        console.log(`💾 [DPI_PERSISTENCE] Saved DPI: ${dpi}`);
      } else {
        console.warn(`⚠️ [DPI_PERSISTENCE] Invalid DPI value: ${dpi}, not saving`);
      }
    } catch (error) {
      console.warn(`⚠️ [DPI_PERSISTENCE] Failed to save DPI:`, error);
    }
  }

  /**
   * Load DPI setting from localStorage
   */
  static loadDPI(): number {
    try {
      const savedDPI = localStorage.getItem(DPI_STORAGE_KEY);
      if (savedDPI) {
        const dpi = parseInt(savedDPI, 10);
        if (dpi >= 72 && dpi <= 600) {
          console.log(`📂 [DPI_PERSISTENCE] Loaded DPI: ${dpi}`);
          return dpi;
        } else {
          console.warn(`⚠️ [DPI_PERSISTENCE] Invalid saved DPI: ${dpi}, using default`);
        }
      }
    } catch (error) {
      console.warn(`⚠️ [DPI_PERSISTENCE] Failed to load DPI:`, error);
    }
    
    console.log(`📂 [DPI_PERSISTENCE] Using default DPI: ${DEFAULT_DPI}`);
    return DEFAULT_DPI;
  }

  /**
   * Clear saved DPI (for testing or reset)
   */
  static clearDPI(): void {
    try {
      localStorage.removeItem(DPI_STORAGE_KEY);
      console.log(`🗑️ [DPI_PERSISTENCE] Cleared saved DPI`);
    } catch (error) {
      console.warn(`⚠️ [DPI_PERSISTENCE] Failed to clear DPI:`, error);
    }
  }

  /**
   * Check if DPI is currently saved
   */
  static hasSavedDPI(): boolean {
    try {
      return localStorage.getItem(DPI_STORAGE_KEY) !== null;
    } catch (error) {
      return false;
    }
  }
}








